"use client"

import { useCallback, useEffect, useState, useRef } from "react"
import { Quote, ChevronLeft, ChevronRight, MessageSquare } from "lucide-react"
import axios from "axios"

export default function Testimonials2({ profile }) {
  const [activeSlide, setActiveSlide] = useState(0)
  const intervalRef = useRef(null)
  const ref = useRef(null)
  const [isInView, setIsInView] = useState(false)
  const [testimonials, setTestimonials] = useState([])

  useEffect(() => {
    // Fetch testimonials for this professional
    const fetchTestimonials = async () => {
      if (!profile || !profile.professional_id) return
      try {
        const res = await axios.get(
          `http://localhost:5000/professional-testimonials/${profile.professional_id}`,
        )
        setTestimonials(res.data)
      } catch (err) {
        setTestimonials([])
      }
    }
    fetchTestimonials()
  }, [profile])

  const startAutoSlide = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }
    if (testimonials.length > 1) {
      intervalRef.current = setInterval(() => {
        setActiveSlide((prev) => (prev + 1) % testimonials.length)
      }, 5000)
    }
  }, [testimonials.length])

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 },
    )

    const observedElement = ref.current
    if (observedElement) {
      observer.observe(observedElement)
    }

    return () => {
      if (observedElement) {
        observer.unobserve(observedElement)
      }
    }
  }, [])

  useEffect(() => {
    startAutoSlide()
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [startAutoSlide])

  const goToSlide = (index) => {
    setActiveSlide(index)
    startAutoSlide()
  }

  const nextSlide = () => {
    setActiveSlide((prev) => (prev + 1) % testimonials.length)
    startAutoSlide()
  }

  const prevSlide = () => {
    setActiveSlide((prev) => (prev - 1 + testimonials.length) % testimonials.length)
    startAutoSlide()
  }

  return (
    <section ref={ref} id="testimonial" className="relative py-20 px-4 overflow-hidden bg-slate-50">
      {/* Background elements */}
      <div className="absolute top-20 left-0 w-72 h-72 bg-teal-100/30 rounded-full blur-3xl -z-10" />
      <div className="absolute bottom-20 right-0 w-72 h-72 bg-rose-100/30 rounded-full blur-3xl -z-10" />

      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12 space-y-2">
          <div className="flex items-center justify-center gap-2">
            <MessageSquare className="h-5 w-5 text-teal-500" />
            <h2 className="text-3xl font-bold">Testimonials</h2>
          </div>
          <p className="text-slate-600 max-w-2xl mx-auto">
            What businesses said about working with me
          </p>
        </div>

        <div className="relative max-w-3xl mx-auto">
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${activeSlide * 100}%)` }}
            >
              {testimonials.length === 0 ? (
                <div className="w-full flex-shrink-0 px-4">
                  <div className="h-full shadow-md rounded-lg border border-gray-200 bg-white flex items-center justify-center min-h-[200px]">
                    <span className="text-slate-500">No testimonials yet.</span>
                  </div>
                </div>
              ) : (
                testimonials.map((testimonial, idx) => (
                  <div key={idx} className="w-full flex-shrink-0 px-4">
                    <div
                      className={`h-full shadow-md transition-all duration-700 rounded-lg border border-gray-200 bg-white ${
                        isInView ? "opacity-100 scale-100" : "opacity-0 scale-95"
                      }`}
                    >
                      <div className="p-8 text-center">
                        <div className="relative mx-auto w-16 h-16 mb-6">
                          <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-rose-400 rounded-full blur-sm -z-10 scale-95 opacity-30" />
                          <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white flex items-center justify-center bg-gray-100">
                            <span className="text-2xl font-bold text-teal-500">
                              {testimonial.client_name?.charAt(0) || "C"}
                            </span>
                          </div>
                          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-white rounded-full p-1 shadow-sm">
                            <Quote className="h-4 w-4 text-teal-500" />
                          </div>
                        </div>

                        <p className="text-slate-700 italic mb-6">
                          {testimonial.comment || "No comment provided."}
                        </p>

                        <div className="space-y-1">
                          <h4 className="font-semibold">{testimonial.client_name || "Client"}</h4>
                          <p className="text-sm text-slate-500">
                            {testimonial.rating ? (
                              <span>
                                {Array.from({ length: testimonial.rating }, (_, i) => (
                                  <span key={i} className="text-yellow-400">
                                    &#9733;
                                  </span>
                                ))}
                                {Array.from({ length: 5 - testimonial.rating }, (_, i) => (
                                  <span key={i} className="text-gray-300">
                                    &#9733;
                                  </span>
                                ))}
                              </span>
                            ) : (
                              "No rating"
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Navigation buttons */}
          {testimonials.length > 1 && (
            <div className="flex justify-between items-center mt-8">
              <button
                onClick={prevSlide}
                className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
                <span className="sr-only">Previous</span>
              </button>

              <div className="flex gap-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`w-2.5 h-2.5 rounded-full transition-colors ${
                      activeSlide === index ? "bg-teal-500" : "bg-slate-300 hover:bg-slate-400"
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>

              <button
                onClick={nextSlide}
                className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors"
              >
                <ChevronRight className="h-4 w-4" />
                <span className="sr-only">Next</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
